import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import Section from '../../../../components/Main/Section/Section';
import Guide from '../../../../components/Guide/Guide';
import React, { ChangeEvent, KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import Chip from '../../../../components/Chip/Chip';
import styled from 'styled-components';
import KeywordMonitoringService from '../../../../services/KeywordMonitoringService';
import { IKeywordMonitoring } from '../../../../shared/interfaces/IKeywordMonitoring';
import { IKeywordSearch } from '../../../../shared/interfaces/IKeywordSearch';
import { useTranslation } from 'react-i18next';
import { ReactComponent as KeywordsImage } from '../../../../assets/images/keywords_no_content.svg';
import { CurrentViewContext } from '../../../../providers/CurrentViewProvider';

const KeywordList = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-height: 300px;
  overflow-y: auto;
  padding: ${(props) => props.theme.spacing(1)} 0;
`;

const KeywordMonitoringGuide = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [hasMonitoringSetUp, setHasMonitoringSetUp] = useState(false);
  const [country, setCountry] = useState<string>('DE');
  const [currentStep, setCurrentStep] = useState(0);
  const [apply, setApplyEnabled] = useState(false);
  const keywordInputRef = useRef<HTMLInputElement | null>(null);
  const [monitorKeywords, setMonitorKeywords] = useState<IKeywordSearch[]>([]);
  const [addWebsite, setAddWebsite] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');

  const handleKeywordInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (keywordInputRef.current) {
      if (keywordInputRef.current.value === '') {
        setApplyEnabled(false);
      } else {
        setApplyEnabled(true);
        keywordInputRef.current.value = event.target.value;
      }
    }
  };

  const addNewKeyword = () => {
    if (!keywordInputRef.current) return;
    if (keywordInputRef.current.value.trim() === '') return;
    const value = keywordInputRef.current.value;
    if (
      monitorKeywords.findIndex((v) => v.search.toLowerCase() === value.trim().toLowerCase()) !== -1
    ) {
      keywordInputRef.current.value = '';
      return;
    }
    setMonitorKeywords((prevState) => {
      return [
        ...prevState,
        { country: country, device: 'desktop', search: value.trim() },
      ].reverse() as IKeywordSearch[];
    });
    keywordInputRef.current.value = '';
    setApplyEnabled(false);
  };

  const getKeywordsForSubmit = () => {
    if (websiteUrl) {
      return monitorKeywords.map((keyword) => ({ ...keyword, url: websiteUrl }));
    }
    return monitorKeywords;
  };

  const checkHasMonitoringSetUp = async (signal: AbortSignal) => {
    const monitoring: IKeywordMonitoring[] = await KeywordMonitoringService.getMonitoring(
      setLoading,
      signal
    );
    if (!monitoring) return;
    setHasMonitoringSetUp(monitoring.length > 0);
  };

  const startMonitoringKeywords = async (keywords: IKeywordSearch[]) => {
    const response = await KeywordMonitoringService.startMonitoring(
      getKeywordsForSubmit(),
      setLoading
    );

    if (!response) return false;

    setMonitorKeywords([]);
    setHasMonitoringSetUp(true);

    return true;
  };

  useEffect(() => {
    const controller = new AbortController();

    (async () => await checkHasMonitoringSetUp(controller.signal))();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    setCurrentView(`${t('nav_keywords')} Guide`);
    document.title = `${t('app_branding')} ${t('nav_keywords')} Guide`;
  }, [setCurrentView, t]);

  function getFaqLink() {
    return process.env.REACT_APP_FAQ_LINK ?? '';
  }

  const alert = (
    <Alert
      severity="warning"
      sx={{
        maxWidth: '650px',
        my: theme.spacing(6),
      }}
    >
      <AlertTitle sx={{ fontWeight: '700' }}>{t('monitoring_alert_title')}</AlertTitle>
      {t('keyword_alert_text')}
      <Link to={getFaqLink()}>FAQ´s.</Link>
    </Alert>
  );

  const finishButton = (
    <Button
      disabled={monitorKeywords.length === 0 || loading}
      variant="contained"
      onClick={async () => {
        const success = await startMonitoringKeywords(monitorKeywords);
        if (success) navigate(-1);
      }}
      sx={{ ml: theme.spacing(1) }}
    >
      {t('finish')}
    </Button>
  );

  const nextButton = (disabled: boolean) => (
    <Button
      disabled={disabled}
      variant="contained"
      onClick={() => setCurrentStep((prevState) => prevState + 1)}
      sx={{ ml: theme.spacing(1) }}
    >
      {t('next')}
    </Button>
  );

  const backButton = (
    <Button
      variant="contained"
      onClick={() => setCurrentStep((prevState) => prevState - 1)}
    >
      {t('back')}
    </Button>
  );

  const cancelButton = (
    <Button
      disabled={loading}
      variant="contained"
      color="warning"
      onClick={() => {
        navigate(-1);
      }}
      sx={{ ml: theme.spacing(1) }}
    >
      {t('back')}
    </Button>
  );

  return (
    <Section>
      <Guide
        image={
          <KeywordsImage
            height="180"
            style={{ marginBottom: !hasMonitoringSetUp ? 0 : `${theme.spacing(6)}` }}
          />
        }
        alert={!hasMonitoringSetUp && alert}
      >
        <Box sx={{ maxWidth: 600 }}>
          <Stepper
            activeStep={currentStep}
            orientation="vertical"
          >
            <Step>
              <StepLabel>{t('select_country')}</StepLabel>
              <StepContent>
                <FormControl
                  size="small"
                  sx={{ minWidth: '284px' }}
                >
                  <InputLabel id="country-select-label">{t('country')}</InputLabel>
                  <Select
                    labelId="country-select-label"
                    value={country}
                    onChange={(event: SelectChangeEvent) => {
                      setCountry(event.target.value);
                    }}
                    label={t('country')}
                  >
                    <MenuItem value="DE">{t('country_germany')}</MenuItem>
                    <MenuItem value="AT">{t('country_austria')}</MenuItem>
                    <MenuItem value="CH">{t('country_switzerland')}</MenuItem>
                    <MenuItem value="NL">{t('country_netherlands')}</MenuItem>
                    <MenuItem value="UK">{t('country_united_kingdom')}</MenuItem>
                    <MenuItem value="US">{t('country_us')}</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ mt: theme.spacing(1) }}>
                  {nextButton(false)}
                  {cancelButton}
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t('keyword_guide_label')}</StepLabel>
              <StepContent>
                <TextField
                  disabled={loading}
                  label={t('keywords')}
                  placeholder={t('example_company_placeholder')}
                  variant="outlined"
                  size="small"
                  inputRef={keywordInputRef}
                  onChange={handleKeywordInputChange}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter') addNewKeyword();
                  }}
                  sx={{ minWidth: '400px' }}
                  autoFocus
                />
                <FormControlLabel
                  control={
                    <Tooltip title={t('add_website_tooltip')}>
                      <Checkbox
                        checked={addWebsite}
                        onChange={(event) => setAddWebsite(event.target.checked)}
                        disabled={loading}
                      />
                    </Tooltip>
                  }
                  label={t('add_website')}
                />
                <Box sx={{ display: 'flex', mt: theme.spacing(1) }}>
                  {backButton}
                  <Button
                    disabled={!apply || loading}
                    variant="contained"
                    onClick={addNewKeyword}
                    sx={{ ml: theme.spacing(1) }}
                  >
                    {t('add_keyword')}
                  </Button>
                  {!addWebsite && finishButton}
                  {addWebsite && nextButton(monitorKeywords.length === 0 || loading)}
                  {cancelButton}
                </Box>
              </StepContent>
            </Step>
            {addWebsite && (
              <Step>
                <StepLabel>{t('website_optional')}</StepLabel>
                <StepContent>
                  <TextField
                    disabled={loading}
                    label={t('enter_website_url')}
                    placeholder="www.example.com"
                    variant="outlined"
                    size="small"
                    value={websiteUrl}
                    onChange={(event) => setWebsiteUrl(event.target.value)}
                    sx={{ minWidth: '400px', mt: 1 }}
                  />
                  <Box sx={{ mt: theme.spacing(1) }}>
                    {backButton}
                    {finishButton}
                    {cancelButton}
                  </Box>
                </StepContent>
              </Step>
            )}
          </Stepper>
        </Box>
        <Box sx={{ display: 'flex', minWidth: '1px', minHeight: '300px', mx: theme.spacing(6) }}>
          <Divider
            orientation="vertical"
            flexItem
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'flex-start',
            minWidth: '250px',
            maxWidth: '300px',
          }}
        >
          <Typography
            variant="body2"
            sx={{ py: theme.spacing(1) }}
          >
            {t('your_keywords')}
          </Typography>
          <KeywordList>
            {monitorKeywords.map((keyword, index) => {
              return (
                <Chip
                  key={index}
                  label={keyword.search}
                  onDelete={() => {
                    setMonitorKeywords((prevState) => {
                      return [...prevState].filter((currentKeyword) => currentKeyword !== keyword);
                    });
                  }}
                />
              );
            })}
          </KeywordList>
          <Button
            disabled={monitorKeywords.length === 0 || loading}
            variant="contained"
            color="error"
            onClick={() => setMonitorKeywords(() => [])}
            sx={{ mt: 1 }}
          >
            {t('reset')}
          </Button>
        </Box>
      </Guide>
    </Section>
  );
};

export default KeywordMonitoringGuide;
